import { HDate } from '@hebcal/core';

/**
 * Compute seasonal prayer insertions.
 *
 * Mashiv HaRuach: Musaf of 22 Tishrei → Musaf of 15 Nisan
 * Morid HaTal: Summer — varies by edah/location
 * Veten Tal Umatar: 7 Cheshvan (IL) / ~Dec 4-5 (diaspora) → Erev Pesach
 *
 * @param {HDate} hdate
 * @param {boolean} isIsrael
 * @param {string} edah - 'ashkenaz' | 'sephardic' | 'mizrahi'
 * @param {Date} civilDate - JS Date for diaspora tal umatar calculation
 * @returns {object}
 */
export function computeSeasonalPrayers(hdate, isIsrael, edah, civilDate) {
  const m = hdate.getMonth();
  const d = hdate.getDate();

  // ═══ Mashiv HaRuach ═══
  // Winter: from Musaf of Shmini Atzeret (22 Tishrei) to Musaf of 1st Pesach (15 Nisan)
  const isMashivHaruachSeason = isInMashivHaruachSeason(m, d);
  const isStartTransition = (m === 7 && d === 22); // Shmini Atzeret
  const isEndTransition = (m === 1 && d === 15);   // 1st Pesach

  // ═══ Morid HaTal ═══
  // Summer replacement — edah/location dependent
  const isSummer = !isMashivHaruachSeason && !isStartTransition;
  let moridHatal;
  if (isSummer || isEndTransition) {
    // Ashkenaz-diaspora: says nothing. Everyone else: says "מוריד הטל"
    const isAshkenazDiaspora = edah === 'ashkenaz' && !isIsrael;
    moridHatal = {
      applicable: true,
      text: isAshkenazDiaspora ? 'omit' : 'morid_hatal',
    };
  } else {
    moridHatal = { applicable: false, text: 'omit' };
  }

  // ═══ Veten Tal Umatar ═══
  const vetenTalUmatar = isInVetenTalUmatarSeason(hdate, isIsrael, civilDate);

  return {
    mashivHaruach: isMashivHaruachSeason,
    moridHatal,
    vetenTalUmatar,
    vetenBracha: !vetenTalUmatar,
    mashivHaruachTransition: {
      isTransitionDay: isStartTransition || isEndTransition,
      startsAtMusaf: isStartTransition,
      endsAtMusaf: isEndTransition,
    },
    talUmatarTransition: {
      isTransitionDay: isTalUmatarTransitionDay(hdate, isIsrael, civilDate),
      startsAtMaariv: isTalUmatarTransitionDay(hdate, isIsrael, civilDate),
    },
  };
}

/**
 * Mashiv HaRuach season: 22 Tishrei (Shmini Atzeret) → 14 Nisan (Erev Pesach).
 * On 22 Tishrei itself: starts at Musaf (partially in season).
 * On 15 Nisan: ends at Musaf (partially in season).
 */
function isInMashivHaruachSeason(m, d) {
  // Tishrei 22 - end of Tishrei
  if (m === 7 && d >= 22) return true;
  // Cheshvan - Adar (months 8-13 in Hebrew calendar)
  if (m >= 8) return true;
  // Nisan 1-14
  if (m === 1 && d <= 14) return true;
  return false;
}

/**
 * Veten Tal Umatar season.
 * Israel: 7 Cheshvan → 14 Nisan (Erev Pesach)
 * Diaspora: 60 days after Tekufat Tishrei → Erev Pesach
 */
function isInVetenTalUmatarSeason(hdate, isIsrael, civilDate) {
  const m = hdate.getMonth();
  const d = hdate.getDate();

  if (isIsrael) {
    // 7 Cheshvan → 14 Nisan
    if (m === 8 && d >= 7) return true;
    if (m >= 9) return true; // Kislev onwards
    if (m === 1 && d <= 14) return true;
    return false;
  }

  // Diaspora: ~Dec 4/5 → Erev Pesach
  const startDate = getDiasporaTalUmatarStart(civilDate.getFullYear());
  if (civilDate >= startDate) {
    // Check we haven't passed Pesach (14 Nisan)
    if (m === 1 && d <= 14) return true;
    if (m >= 8) return true; // After start date in winter
    return true;
  }
  // In spring months before Pesach
  if (m === 1 && d <= 14) return true;
  if (m >= 8 && m <= 13) return true;
  return false;
}

/**
 * Get diaspora Tal Umatar start date.
 * 60 days after Tekufat Tishrei (Julian autumn equinox).
 * Currently: Dec 4 in regular years, Dec 5 in year before civil leap year.
 * After 2100: shifts by one day.
 */
function getDiasporaTalUmatarStart(civilYear) {
  // The civil year in which the Jewish year's winter falls
  // Tekufat Tishrei currently falls on Oct 7/8 (Julian), which is Oct 20/21 (Gregorian)
  // 60 days later = Dec 4/5
  const nextYear = civilYear + 1;
  const isBeforeLeap = nextYear % 4 === 0 && (nextYear % 100 !== 0 || nextYear % 400 === 0);
  const day = isBeforeLeap ? 5 : 4;

  // After 2100, shift by 1 (Gregorian century correction)
  const shift = civilYear >= 2100 ? 1 : 0;

  return new Date(civilYear, 11, day + shift); // December
}

function isTalUmatarTransitionDay(hdate, isIsrael, civilDate) {
  const m = hdate.getMonth();
  const d = hdate.getDate();

  if (isIsrael) {
    return m === 8 && d === 7; // 7 Cheshvan
  }

  // Diaspora: check if today is the start date
  const startDate = getDiasporaTalUmatarStart(civilDate.getFullYear());
  return civilDate.getFullYear() === startDate.getFullYear() &&
    civilDate.getMonth() === startDate.getMonth() &&
    civilDate.getDate() === startDate.getDate();
}
